import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Message } from 'rsuite';
import DatePicker from '../shared/DatePicker';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { showToast } from '../../utils/toast';
import { useDeleteTimesheetEntry, useSaveTimesheetEntry } from '../../hooks/useTimesheet';
import {
  ENTRY_SOURCE,
  dateFromIso,
  formatHours,
  formatLongDate,
  isoFromDate,
  parseDurationInput,
  secondsToClock,
  secondsToHours,
  shiftClock,
} from '../../utils/timesheet';
import { ClockPicker, Switch, TaskPicker, clockSpanSeconds } from './TimesheetParts';

// Start and end times are the default; `mode: 'hours'` is the fallback for an
// entry that only has a length (imported, from the grid, or a meal-break marker).
const blankDraft = (seed = {}) => ({
  mode: 'range',
  taskId: seed.taskId || null,
  date: seed.date || null,
  duration: '',
  startTime: seed.startTime || null,
  endTime: null,
  description: seed.description || '',
  rdEligible: false,
});

/**
 * Add or edit one time entry.
 *
 * `entry` is an existing entry to edit, or null to add one; `seed` prefills a
 * new one ({ taskId, date }) - which is how a click on an empty grid cell
 * opens straight onto that task and day. `readOnly` shows the entry without
 * the controls, for a locked period or somebody else's timesheet.
 */
export default function EntryModal({
  open,
  onClose,
  entry: entryProp = null,
  seed = null,
  employeeId = null,
  tasks = [],
  config = null,
  readOnly = false,
}) {
  const saveMutation = useSaveTimesheetEntry();
  const deleteMutation = useDeleteTimesheetEntry();
  // The entry while open, and the last one once closing, so the dialog doesn't turn
  // into "Log Time" while it fades out. A null entry while open is an add.
  const entry = useLastPresent(open ? { entry: entryProp } : null)?.entry ?? null;
  const [draft, setDraft] = useState(blankDraft());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConfirmDelete(false);
    setError(null);
    if (entry) {
      setDraft({
        mode: entry.startTime && entry.endTime ? 'range' : 'hours',
        taskId: entry.taskId,
        date: entry.date,
        duration: secondsToClock(entry.durationSeconds),
        startTime: entry.startTime || null,
        endTime: entry.endTime || null,
        description: entry.description || '',
        rdEligible: Boolean(entry.rdEligible),
      });
    } else {
      // A new entry starts from its task's answer.
      const seeded = (tasks || []).find((t) => t.id === seed?.taskId);
      setDraft({ ...blankDraft(seed || {}), rdEligible: Boolean(seeded?.effectiveRdEligible) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry, seed]);

  const taskById = useMemo(() => new Map((tasks || []).map((t) => [t.id, t])), [tasks]);
  const task = taskById.get(draft.taskId) || null;
  const exemptRoles = new Set(config?.descriptionExemptRoles || []);
  const needsDescription = Boolean(config?.settings?.requireDescription) && !exemptRoles.has(task?.systemRole);
  const source = entry ? ENTRY_SOURCE[entry.source] : null;
  const locked = readOnly || Boolean(entry?.locked);

  const rangeSeconds = clockSpanSeconds(draft.startTime, draft.endTime);
  const durationSeconds = draft.mode === 'range' ? rangeSeconds : parseDurationInput(draft.duration);
  const durationOk = durationSeconds !== null
    && durationSeconds >= 0
    && durationSeconds <= 86400;
  const canSave = !locked && draft.taskId && draft.date && durationOk && (!needsDescription || draft.description.trim());

  const save = async () => {
    if (!canSave) return;
    setError(null);
    try {
      await saveMutation.mutateAsync({
        id: entry?.id,
        employeeId: entry ? undefined : employeeId || undefined,
        taskId: draft.taskId,
        date: draft.date,
        durationSeconds,
        startTime: draft.mode === 'range' ? draft.startTime : null,
        endTime: draft.mode === 'range' ? draft.endTime : null,
        description: draft.description.trim(),
        rdEligible: draft.rdEligible,
      });
      showToast('success', entry ? 'Entry updated.' : 'Time logged.');
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  const remove = async () => {
    if (!entry) return;
    try {
      await deleteMutation.mutateAsync(entry.id);
      showToast('success', 'Entry deleted.');
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  const title = locked ? 'Time Entry' : entry ? 'Edit Time Entry' : 'Log Time';

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {source && entry?.source === 'time_off' ? (
          <Message type="info" showIcon className="mb-3">{source.label}</Message>
        ) : null}
        {locked && entry?.source !== 'time_off' ? (
          <Message type="info" showIcon className="mb-3">
            {readOnly ? 'You can view this entry but not change it.' : 'This pay period has been submitted, so its time is locked.'}
          </Message>
        ) : null}
        {error ? <Message type="error" showIcon className="mb-3">{error}</Message> : null}

        <Form fluid onSubmit={save}>
          <Form.Group controlId="ts_entry_task">
            <Form.ControlLabel>Project / task</Form.ControlLabel>
            <TaskPicker
              tasks={tasks}
              value={draft.taskId}
              // A different task brings its own R&D answer.
              onChange={(taskId) => setDraft((prev) => ({
                ...prev,
                taskId,
                rdEligible: Boolean(taskById.get(taskId)?.effectiveRdEligible),
              }))}
              disabled={locked}
            />
            {task ? (
              <Form.HelpText>
                {task.billable ? 'Billable' : 'Non-billable'}
                {task.code ? ` · ${task.code}` : ''}
              </Form.HelpText>
            ) : null}
          </Form.Group>

          {task ? (
            <div className="mb-3">
              <Switch
                checked={draft.rdEligible}
                onChange={(rdEligible) => setDraft((prev) => ({ ...prev, rdEligible }))}
                label="R&D credit eligible"
                disabled={locked}
              />
            </div>
          ) : null}

          <Form.Group controlId="ts_entry_date">
            <Form.ControlLabel>Date</Form.ControlLabel>
            <DatePicker
              value={draft.date ? dateFromIso(draft.date) : null}
              onChange={(value) => setDraft((prev) => ({ ...prev, date: value ? isoFromDate(value) : null }))}
              format="MM/dd/yyyy"
              oneTap
              block
              cleanable={false}
              isoWeek={false}
              weekStart={0}
              disabled={locked}
              placement="bottomStart"
            />
            {draft.date ? <Form.HelpText>{formatLongDate(draft.date)}</Form.HelpText> : null}
          </Form.Group>

          {draft.mode === 'range' ? (
            <div className="d-flex gap-3 flex-wrap align-items-start">
              <Form.Group controlId="ts_entry_start" style={{ flex: '1 1 140px' }}>
                <Form.ControlLabel>Start</Form.ControlLabel>
                <ClockPicker value={draft.startTime} onChange={(startTime) => setDraft((prev) => ({ ...prev, startTime }))} aria-label="Start time" disabled={locked} fallback={shiftClock(draft.endTime, -30)} />
              </Form.Group>
              <Form.Group controlId="ts_entry_end" style={{ flex: '1 1 140px' }}>
                <Form.ControlLabel>End</Form.ControlLabel>
                <ClockPicker value={draft.endTime} onChange={(endTime) => setDraft((prev) => ({ ...prev, endTime }))} aria-label="End time" disabled={locked} fallback={shiftClock(draft.startTime, 30)} anchor={draft.startTime} />
              </Form.Group>
              <Form.Group style={{ flex: '0 0 100px' }}>
                <Form.ControlLabel>Hours</Form.ControlLabel>
                <div className="fw-bold bdc-num pt-2">
                  {rangeSeconds !== null ? formatHours(secondsToHours(rangeSeconds)) : <span className="nsi-table__muted fw-normal">—</span>}
                </div>
              </Form.Group>
            </div>
          ) : (
            <Form.Group controlId="ts_entry_duration">
              <Form.ControlLabel>Hours</Form.ControlLabel>
              <Input
                value={draft.duration}
                onChange={(value) => setDraft((prev) => ({ ...prev, duration: value }))}
                placeholder="1:30 or 1.5"
                inputMode="decimal"
                disabled={locked}
                style={{ maxWidth: 160 }}
              />
              {draft.duration && parseDurationInput(draft.duration) === null ? <Form.HelpText>Try 1:30, 1.5 or 90m</Form.HelpText> : null}
            </Form.Group>
          )}
          {draft.mode === 'range' && draft.startTime && draft.endTime && rangeSeconds === null ? (
            <Message type="warning" showIcon className="mb-3">The end time has to be after the start time.</Message>
          ) : null}
          {!locked ? (
            <p className="mb-3">
              <Button
                size="sm"
                onClick={() => setDraft((prev) => (prev.mode === 'range'
                  ? { ...prev, mode: 'hours', duration: rangeSeconds ? secondsToClock(rangeSeconds) : prev.duration }
                  : { ...prev, mode: 'range' }))}
              >
                {draft.mode === 'range' ? 'Enter hours instead' : 'Use start and end times'}
              </Button>
            </p>
          ) : null}

          <Form.Group controlId="ts_entry_description">
            <Form.ControlLabel>
              What did you work on?{needsDescription ? '' : <span className="text-muted fw-normal"> (optional)</span>}
            </Form.ControlLabel>
            <Input
              as="textarea"
              rows={3}
              value={draft.description}
              onChange={(value) => setDraft((prev) => ({ ...prev, description: value }))}
              placeholder="e.g. Drafted the site plan revisions for the client review"
              disabled={locked}
            />
          </Form.Group>
          {source && entry?.source !== 'time_off' ? (
            <p className="text-muted small mb-0"><i className={`bi ${source.icon} me-1`} aria-hidden="true" />{source.label}</p>
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer
        left={entry && !locked ? (
          confirmDelete ? (
            <Button variant="danger-solid" icon="trash" loading={deleteMutation.isPending} onClick={remove}>
              Confirm delete
            </Button>
          ) : (
            <Button variant="danger" icon="trash" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )
        ) : null}
      >
        <Button onClick={onClose}>{locked ? 'Close' : 'Cancel'}</Button>
        {!locked ? (
          <Button variant="primary" onClick={save} disabled={!canSave} loading={saveMutation.isPending}>
            {entry ? 'Save' : 'Log time'}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
